import { Link } from 'react-router-dom'
import { Clock } from 'lucide-react'
import { AuthLayout } from '@/modules/auth/ui/components'
import { formatTryAgainLabel } from '@/shared/ui/hooks'
import { Button } from '@/shared/ui/primitives'
import type { InviteRenewalState } from '../invite-renewal-state'

export type ExpiredStateProps = {
  organizationName?: string
  renewable: boolean
  renewalState: InviteRenewalState
  renewalError: string
  cooldownSeconds: number
  onRequestNewInvite: () => void
}

const DEAD_END_COPY =
  'For security reasons, invitation links expire after 30 days. Please ask the person who invited you for a new link.'

const RENEWABLE_COPY =
  'For security reasons, invitation links expire after 30 days. Request a new one and we will send a fresh link to the email this invitation was originally sent to.'

const CONFIRMATION_COPY =
  'A new invitation is on its way. Check the inbox for the email address this invitation was sent to.'

function bodyCopy(renewable: boolean, renewalState: InviteRenewalState): string {
  if (!renewable) return DEAD_END_COPY
  if (renewalState === 'sent') return CONFIRMATION_COPY
  return RENEWABLE_COPY
}

export function ExpiredState({
  organizationName,
  renewable,
  renewalState,
  renewalError,
  cooldownSeconds,
  onRequestNewInvite,
}: ExpiredStateProps) {
  const subtitle = organizationName
    ? `Your invitation to join ${organizationName} has expired.`
    : 'This invitation link has expired.'
  const showRequestButton = renewable && renewalState !== 'sent'

  return (
    <AuthLayout title="Invitation expired" subtitle={subtitle}>
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Clock className="h-8 w-8 text-primary" aria-hidden />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{bodyCopy(renewable, renewalState)}</p>
        <Button asChild variant="outline" className="w-full">
          <Link to="/sign-in">Go to sign in</Link>
        </Button>
        {showRequestButton && renewalError !== '' ? (
          <p role="alert" className="text-sm text-destructive">
            {renewalError}
          </p>
        ) : null}
        {showRequestButton ? (
          <Button
            type="button"
            className="w-full"
            onClick={onRequestNewInvite}
            disabled={renewalState === 'sending' || cooldownSeconds > 0}
          >
            {renewalState === 'sending'
              ? 'Sending…'
              : cooldownSeconds > 0
                ? formatTryAgainLabel(cooldownSeconds)
                : 'Request new invite'}
          </Button>
        ) : null}
      </div>
    </AuthLayout>
  )
}
